/**
 * useReports -- Zustand store for performance reporting.
 */
import { create } from 'zustand'
import {
  getMonthlyReport,
  getFundSummary,
  getPerformanceAttribution,
  generatePdfReport,
  openExportReport,
  getPerformanceSeries,
  getMonthlyReturns,
  getRollingSharpe,
  getRollingDrawdown,
  getWinLossStreak,
} from '@/lib/api'
import type {
  MonthlyReport,
  FundSummary,
  PerformanceAttribution,
  NavPoint,
  RollingSharpePoint,
  DrawdownPoint,
  MonthlyReturnEntry,
  WinLossStreak,
} from '@/lib/types'

interface ReportsState {
  monthly: MonthlyReport | null
  summary: FundSummary | null
  attribution: PerformanceAttribution | null
  performanceSeries: NavPoint[]
  rollingSharpe: RollingSharpePoint[]
  rollingDrawdown: DrawdownPoint[]
  monthlyReturns: MonthlyReturnEntry[]
  winLossStreak: WinLossStreak | null
  loading: boolean
  loadingSummary: boolean
  loadingAttribution: boolean
  loadingPerformance: boolean
  error: string | null
  selectedMonth: string

  fetchMonthly: (month?: string) => Promise<void>
  fetchSummary: () => Promise<void>
  fetchAttribution: () => Promise<void>
  fetchPerformanceData: (days?: number, window?: number) => Promise<void>
  exportReport: (month?: string) => Promise<void>
  openExport: (month?: string) => Promise<void>
  setMonth: (month: string) => void
}

function currentMonth(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

export const useReports = create<ReportsState>()((set, get) => ({
  monthly: null,
  summary: null,
  attribution: null,
  performanceSeries: [],
  rollingSharpe: [],
  rollingDrawdown: [],
  monthlyReturns: [],
  winLossStreak: null,
  loading: false,
  loadingSummary: false,
  loadingAttribution: false,
  loadingPerformance: false,
  error: null,
  selectedMonth: currentMonth(),

  fetchMonthly: async (month?: string) => {
    const m = month ?? get().selectedMonth
    set({ loading: true, error: null })
    try {
      const data = await getMonthlyReport(m)
      set({ monthly: data, loading: false, selectedMonth: m })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch monthly report'
      set({ loading: false, error: msg })
    }
  },

  fetchSummary: async () => {
    set({ loadingSummary: true })
    try {
      const data = await getFundSummary()
      set({ summary: data, loadingSummary: false })
    } catch { set({ loadingSummary: false }) }
  },

  fetchAttribution: async () => {
    set({ loadingAttribution: true })
    try {
      const data = await getPerformanceAttribution()
      set({ attribution: data, loadingAttribution: false })
    } catch { set({ loadingAttribution: false }) }
  },

  fetchPerformanceData: async (days = 90, window = 30) => {
    set({ loadingPerformance: true })
    try {
      const [series, sharpe, drawdown, monthly, streak] = await Promise.all([
        getPerformanceSeries(days),
        getRollingSharpe(window),
        getRollingDrawdown(),
        getMonthlyReturns(),
        getWinLossStreak(),
      ])
      set({
        performanceSeries: series,
        rollingSharpe: sharpe,
        rollingDrawdown: drawdown,
        monthlyReturns: monthly,
        winLossStreak: streak,
        loadingPerformance: false,
      })
    } catch { set({ loadingPerformance: false }) }
  },

  exportReport: async (month?: string) => {
    try {
      const { html } = await generatePdfReport(month ?? get().selectedMonth)
      await navigator.clipboard.writeText(html)
    } catch {
      // fallback: open in new window
      try {
        const { html } = await generatePdfReport(month ?? get().selectedMonth)
        const w = window.open('', '_blank')
        if (w) {
          w.document.write(html)
          w.document.close()
        }
      } catch { /* ignore */ }
    }
  },

  openExport: async (month?: string) => {
    await openExportReport(month ?? get().selectedMonth)
  },

  setMonth: (month: string) => set({ selectedMonth: month }),
}))
